import type { PartialDeep } from 'type-fest';

import * as z from 'zod';
import { type Config, configSchema } from '@/config/types.config';
import envConfig from '@env-config';
import type { Translations } from '@ot-translations-types';
import { CacheStorageKeys, getCountryLanguage, isAppEnv } from '@repo/utils';
import { mergeDeep } from '@repo/utils/type.utils';

import type { OrderTakingWindow } from '@/api/types/window.types';
import { getAppJsonConfig, getTranslations } from '@/utils/window.utils';

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, '');

declare const window: OrderTakingWindow;

const OT_ENV = isAppEnv(import.meta.env.VITE_ENV) ? import.meta.env.VITE_ENV : 'prod';

const configOverrideSchema = z.record(z.string(), z.unknown());

const isConfigOverride = (value: unknown): value is PartialDeep<Config> =>
    configOverrideSchema.safeParse(value).success;

const isConfig = (value: unknown): value is Config => configSchema.safeParse(value).success;

export type ConfigSource = 'empty' | 'esi' | 'fetch';
export type TranslationSource = 'bcp' | 'esi' | 'fallback' | 'legacy';

export interface ApplyBuCodeConfigResult {
    buCodeConfigLoaded: boolean;
    buCodeEnvConfigLoaded: boolean;
}

export interface BootstrapApplicationResult {
    country: string;
    language: string;
    marketConfigSource: ConfigSource;
    marketEnvConfigLoaded: boolean;
    translationSource: TranslationSource;
}

interface MarketConfigResult {
    config: PartialDeep<Config>;
    source: ConfigSource;
}

interface MarketTranslationsResult {
    source: TranslationSource;
    translations: Translations;
}

/* v8 ignore start */
const mockApp = async () => {
    if (import.meta.env.DEV && import.meta.env.VITE_MOCK === 'true') {
        const { worker } = await import('@/__mocks__/browser');

        if (import.meta.env.VITE_MOCK_COWORKER === 'true') {
            const { seedMockCoworkerEnvironment } = await import('@/__mocks__/coworker.mock');
            seedMockCoworkerEnvironment();
        }

        return worker.start({
            onUnhandledRequest(req) {
                const host = new URL(req.url).host;
                if (host === window.location.host) {
                    // oxlint-disable-next-line no-console
                    console.warn('Unhandled request', host);
                }
            },
        });
    }
    return;
};
/* v8 ignore stop */

const loadFallbackTranslations = async (): Promise<Translations> => {
    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    const fallbackModule = (await import('@ot-translations')) as { default: Translations };
    return fallbackModule.default;
};

const fetchTranslationFile = async (fileName: string): Promise<Translations | undefined> => {
    try {
        const translationsReq = await fetch(new URL(`/translations/ot-frontend/${fileName}`, window.location.origin));
        if (!translationsReq.ok) {
            return;
        }

        // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
        return (await translationsReq.json()) as Translations;
    } catch {
        return;
    }
};

const fetchMarketTranslations = async (country: string, language: string): Promise<MarketTranslationsResult> => {
    try {
        if (import.meta.env.DEV && import.meta.env.VITE_ENV === 'local') {
            return { source: 'fallback', translations: await loadFallbackTranslations() };
        }

        const translations = getTranslations(); // ESI
        if (Object.keys(translations).length > 0) {
            return { source: 'esi', translations };
        }

        const bcpTranslations = await fetchTranslationFile(`${language}-${country.toUpperCase()}.json`);
        if (bcpTranslations) {
            return { source: 'bcp', translations: bcpTranslations };
        }

        const legacyTranslations = await fetchTranslationFile(`${country}-${language}.json`);
        if (legacyTranslations) {
            return { source: 'legacy', translations: legacyTranslations };
        }

        return { source: 'fallback', translations: await loadFallbackTranslations() };
    } catch {
        return { source: 'fallback', translations: await loadFallbackTranslations() };
    }
};

const fetchMarketConfig = async (country: string): Promise<MarketConfigResult> => {
    try {
        const jsonConfig = import.meta.env.VITE_ENV === 'local' ? {} : getAppJsonConfig(); // if local, use empty object to force fetch from local instead of ESI
        if (jsonConfig && Object.keys(jsonConfig).length > 0) {
            return { config: jsonConfig, source: 'esi' };
        }

        const marketConfig = await fetch(
            new URL(`${BASE_URL}/data/config/${country}.config.json`, window.location.origin)
        );
        const data: unknown = await marketConfig.json();
        return isConfigOverride(data) ? { config: data, source: 'fetch' } : { config: {}, source: 'empty' };
    } catch {
        return { config: {}, source: 'empty' };
    }
};

const fetchConfig = async (path: string): Promise<PartialDeep<Config>> => {
    try {
        const response = await fetch(new URL(`${BASE_URL}/${path}`, window.location.origin));
        if (!response.ok) {
            return {};
        }
        const data: unknown = await response.json();
        return isConfigOverride(data) ? data : {};
    } catch {
        return {};
    }
};

export const applyBuCodeConfig = async (buCode: string): Promise<ApplyBuCodeConfigResult> => {
    const [buCodeConfig, buCodeEnvConfig] = await Promise.all([
        fetchConfig(`data/config/bucode/${buCode}.config.json`),
        fetchConfig(`data/config/bucode/${OT_ENV}.${buCode}.config.json`),
    ]);
    const config = mergeDeep(mergeDeep(window.salja.orders.config, buCodeConfig), buCodeEnvConfig);
    if (isConfig(config)) {
        window.salja.orders.config = config;
    }

    return {
        buCodeConfigLoaded: Object.keys(buCodeConfig).length > 0,
        buCodeEnvConfigLoaded: Object.keys(buCodeEnvConfig).length > 0,
    };
};

export const bootstrapApplication = async (): Promise<BootstrapApplicationResult | undefined> => {
    try {
        const countryLanguage = getCountryLanguage();
        const { country, language } = countryLanguage;
        const [marketConfig, marketTranslations, marketEnvConfig] = await Promise.all([
            fetchMarketConfig(country),
            fetchMarketTranslations(country, language),
            fetchConfig(`data/config/${OT_ENV}.${country}.config.json`),
        ]);

        window.salja.cacheStorageKeys = Object.values(CacheStorageKeys);
        window.salja.orders = {
            ...window.salja.orders,
            text: marketTranslations.translations,
            config: (() => {
                const config = mergeDeep(mergeDeep(envConfig, marketConfig.config), marketEnvConfig);
                return isConfig(config) ? config : envConfig;
            })(),
        };

        /* v8 ignore start */
        if (import.meta.env.MODE === 'development' && import.meta.env.VITE_MOCK === 'true') {
            await mockApp();
        }
        /* v8 ignore stop */

        return {
            country,
            language,
            marketConfigSource: marketConfig.source,
            marketEnvConfigLoaded: Object.keys(marketEnvConfig).length > 0,
            translationSource: marketTranslations.source,
        };
    } catch (error) {
        // oxlint-disable-next-line no-console
        console.error('Error bootstrapping application:', error);
        return;
    }
};
